// Shared daily summary state hook - prevents flicker and recalculation
// Used by Home, Nutrition, and Exercise screens via AppContext.dailySummary
//
// Firestore reads are handled by dailySummaryFetcher.service.ts which guarantees:
// - single-flight (at most one fetch burst in-flight across all callers)
// - TTL cache (3s) to absorb boot-time render churn
// This hook is responsible only for React state management on top of that service.

import { useState, useEffect, useCallback, useRef } from 'react';
import { fetchDailySummaryOnce, invalidateFetcherCache } from '../services/dailySummaryFetcher.service';
import { getTodayDate } from '../utils/date';
import { loadTodaySnapshot, saveTodaySnapshot } from '../services/todaySnapshot.service';
import { markTodaySummaryFetchStart, markTodaySummaryFetchComplete, markAppLaunchComplete } from '../utils/performance';
import type { DailyNutrition, TargetRanges } from '../types/nutrition';
import type { DailyTDEE } from '../types/tdee';
import type { DailyBurn } from '../types/workout';

export interface DailySummary {
  tdee: DailyTDEE | null;
  nutrition: DailyNutrition | null;
  targets: TargetRanges | null;
  bodyMetrics: {
    currentWeight: number;
    currentBodyFat?: number;
  } | null;
  dailyBurn: DailyBurn | null;
  loading: boolean;
  lastLoadedDate: string | null;
}

export interface DailySummaryContextValue extends DailySummary {
  refreshDailySummary: (forceRefresh?: boolean, silent?: boolean) => Promise<void>;
  invalidateCache: () => void;
  /** Instantly patch local totals + global cache with a new entry's macros. Returns a rollback ID. */
  optimisticAddEntry: (macros: { calories: number; protein: number; carbs: number; fat: number; fibre: number }, hydrationMl: number, creatine?: number, collagen?: number) => string;
  /** Undo an optimistic add by rollback ID (on write failure). */
  optimisticRollback: (rollbackId: string) => void;
}

// Module-level React-layer cache — shared across all hook instances
let globalCache: DailySummary | null = null;
let globalCacheDate: string | null = null;
let cacheVersion = 0;
const cacheInvalidationListeners: Set<() => void> = new Set();

export function invalidateDailySummaryCache(): void {
  globalCache = null;
  globalCacheDate = null;
  cacheVersion++;
  invalidateFetcherCache();
  cacheInvalidationListeners.forEach(listener => listener());
}

// uid and isDemoMode are primitives — stable across AppContext re-renders.
// Passing the full user object would cause useCallback to be recreated on every
// AppContext render (new object reference = unstable dep = re-fired useEffect).
export function useDailySummary(uid: string | null, isDemoMode: boolean): DailySummaryContextValue {
  const [summary, setSummary] = useState<DailySummary>(() => {
    const today = getTodayDate();

    if (globalCache && globalCacheDate === today) return globalCache;

    if (globalCache && globalCacheDate !== today) {
      globalCache = null;
      globalCacheDate = null;
      cacheVersion++;
    }

    const cachedSnapshot = loadTodaySnapshot();
    if (cachedSnapshot) {
      globalCache = cachedSnapshot;
      globalCacheDate = today;
      return cachedSnapshot;
    }

    return {
      tdee: null, nutrition: null, targets: null,
      bodyMetrics: null, dailyBurn: null,
      loading: false, lastLoadedDate: null,
    };
  });

  const summaryRef = useRef<DailySummary>(summary);
  useEffect(() => { summaryRef.current = summary; }, [summary]);

  const refreshDailySummary = useCallback(async (forceRefresh: boolean = false, silent: boolean = false) => {
    if (!uid && !isDemoMode) return;

    if (forceRefresh) {
      globalCache = null;
      globalCacheDate = null;
      cacheVersion++;
      // Do NOT call invalidateFetcherCache() here — that would bypass the service-layer
      // single-flight gate. The fetcher handles its own cache; we only clear the React layer.
      // invalidateFetcherCache() is called only after actual writes (save/delete).
    }

    const today = getTodayDate();

    if (!forceRefresh && globalCache && globalCacheDate === today) {
      setSummary(globalCache);
      return;
    }

    const previousSummary = summaryRef.current?.nutrition
      ? summaryRef.current
      : (globalCache?.nutrition ? globalCache : summaryRef.current);

    if (!silent) {
      setSummary((prev) => ({
        ...prev,
        loading: true,
        nutrition: prev.nutrition || previousSummary?.nutrition || null,
      }));
    }

    const fetchStartTime = markTodaySummaryFetchStart();

    try {
      // Single-flight guaranteed by the service layer — concurrent callers
      // await the same promise, no duplicate Firestore reads
      const data = await fetchDailySummaryOnce(uid ?? 'demo-user', today, isDemoMode);

      // After await returns, check if another caller already wrote globalCache for today.
      // If so, the work is done — just apply the cached result and return.
      if (globalCache && globalCacheDate === today) {
        setSummary(globalCache);
        return;
      }

      // When Firestore returns a nutrition object (including a valid zero/empty-day state),
      // always use it — a zero-entry day is ground truth and must overwrite stale values.
      // Only fall back to previous nutrition when the fetch returned null (network failure
      // or no document), to prevent flickering on transient errors.
      const isSameDay = previousSummary?.lastLoadedDate === today;
      let preservedNutrition: DailyNutrition | null = null;

      if (data.nutrition !== null) {
        preservedNutrition = data.nutrition;
      } else if (isSameDay && previousSummary?.nutrition) {
        preservedNutrition = previousSummary.nutrition;
      }

      const newSummary: DailySummary = {
        tdee: data.tdee ?? previousSummary?.tdee ?? null,
        nutrition: preservedNutrition,
        targets: data.targetRanges ?? previousSummary?.targets ?? null,
        bodyMetrics: data.bodyMetrics ?? previousSummary?.bodyMetrics ?? null,
        dailyBurn: data.dailyBurn ?? previousSummary?.dailyBurn ?? null,
        loading: false,
        lastLoadedDate: today,
      };

      globalCache = newSummary;
      globalCacheDate = today;

      saveTodaySnapshot(newSummary);
      markTodaySummaryFetchComplete(fetchStartTime);

      console.log('[useDailySummary] ✅ Fresh data loaded from Firestore:', {
        totalCalories: data.nutrition?.totalCalories,
        totalProtein: data.nutrition?.totalProtein,
        entriesCount: data.nutrition?.entries?.length,
        cacheVersion,
      });

      setSummary(newSummary);
    } catch (error) {
      console.error('[useDailySummary] Failed to load daily summary:', error);
      setSummary((prev) => ({ ...prev, loading: false }));
    }
  }, [uid, isDemoMode]);

  // Load on mount / uid change
  useEffect(() => {
    if (isDemoMode) {
      refreshDailySummary(true, false);
      return;
    }

    if (!uid) {
      setSummary({
        tdee: null, nutrition: null, targets: null,
        bodyMetrics: null, dailyBurn: null,
        loading: false, lastLoadedDate: null,
      });
      return;
    }

    const today = getTodayDate();

    if (globalCache && globalCacheDate === today) {
      setSummary(globalCache);
      refreshDailySummary(false, true);
      return;
    }

    const cachedSnapshot = loadTodaySnapshot();
    if (cachedSnapshot) {
      setSummary(cachedSnapshot);
      globalCache = cachedSnapshot;
      globalCacheDate = today;
      refreshDailySummary(false, true);
      return;
    }

    refreshDailySummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, isDemoMode]);

  // Date rollover detection
  useEffect(() => {
    if (!uid) return;

    function checkDateRollover() {
      const today = getTodayDate();
      if (globalCacheDate && globalCacheDate !== today) {
        globalCache = null;
        globalCacheDate = null;
        cacheVersion++;
        invalidateFetcherCache();
        setSummary({
          tdee: null, nutrition: null, targets: null,
          bodyMetrics: null, dailyBurn: null,
          loading: true, lastLoadedDate: null,
        });
        refreshDailySummary(true, false);
      }
    }

    const onFocus = () => checkDateRollover();
    const onVisibilityChange = () => { if (document.visibilityState === 'visible') checkDateRollover(); };
    const onPageShow = (e: PageTransitionEvent) => { if (e.persisted) checkDateRollover(); };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pageshow', onPageShow);
    const intervalId = setInterval(checkDateRollover, 60_000);
    checkDateRollover();

    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pageshow', onPageShow);
      clearInterval(intervalId);
    };
  }, [uid, refreshDailySummary]);

  // Global cache invalidation listener (e.g. after offline sync)
  useEffect(() => {
    const handleCacheInvalidation = () => refreshDailySummary(true, true);
    cacheInvalidationListeners.add(handleCacheInvalidation);
    return () => { cacheInvalidationListeners.delete(handleCacheInvalidation); };
  }, [refreshDailySummary]);

  // Mark app launch complete when data is ready
  useEffect(() => {
    if (uid && summary.tdee && !summary.loading) markAppLaunchComplete();
  }, [uid, summary.tdee, summary.loading]);

  const invalidateCache = useCallback(() => {
    globalCache = null;
    globalCacheDate = null;
    cacheVersion++;
    invalidateFetcherCache();
    setSummary((prev) => ({
      ...prev, tdee: null, nutrition: null, dailyBurn: null, lastLoadedDate: null,
    }));
  }, []);

  const optimisticRollbacks = useRef<Map<string, {
    calories: number; protein: number; carbs: number;
    fat: number; fibre: number; hydrationMl: number;
  }>>(new Map());

  const optimisticAddEntry = useCallback((
    macros: { calories: number; protein: number; carbs: number; fat: number; fibre: number },
    hydrationMl: number,
    creatine?: number,
    collagen?: number,
  ): string => {
    const rollbackId = `opt-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    optimisticRollbacks.current.set(rollbackId, { ...macros, hydrationMl });

    setSummary((prev) => {
      const prevNutrition = prev.nutrition;
      if (!prevNutrition) {
        const optimisticNutrition = {
          totalCalories: macros.calories, totalProtein: macros.protein,
          totalCarbs: macros.carbs, totalFat: macros.fat,
          totalFibre: macros.fibre, totalHydration: hydrationMl, entries: [],
          totalCreatine: creatine ?? 0,
          totalCollagen: collagen ?? 0,
        } as unknown as DailyNutrition;
        const patched: DailySummary = { ...prev, nutrition: optimisticNutrition };
        globalCache = patched;
        saveTodaySnapshot(patched);
        return patched;
      }
      const patched: DailySummary = {
        ...prev,
        nutrition: {
          ...prevNutrition,
          totalCalories: (prevNutrition.totalCalories || 0) + macros.calories,
          totalProtein: (prevNutrition.totalProtein || 0) + macros.protein,
          totalCarbs: (prevNutrition.totalCarbs || 0) + macros.carbs,
          totalFat: (prevNutrition.totalFat || 0) + macros.fat,
          totalFibre: (prevNutrition.totalFibre || 0) + macros.fibre,
          totalHydration: (prevNutrition.totalHydration || 0) + hydrationMl,
          totalCreatine: (prevNutrition.totalCreatine || 0) + (creatine ?? 0),
          totalCollagen: (prevNutrition.totalCollagen || 0) + (collagen ?? 0),
        } as DailyNutrition,
      };
      globalCache = patched;
      saveTodaySnapshot(patched);
      return patched;
    });

    console.log(`[useDailySummary] Optimistic add (${rollbackId}):`, { ...macros, hydrationMl });
    return rollbackId;
  }, []);

  const optimisticRollback = useCallback((rollbackId: string) => {
    const delta = optimisticRollbacks.current.get(rollbackId);
    if (!delta) {
      console.warn(`[useDailySummary] Rollback ${rollbackId}: no delta found (already rolled back?)`);
      return;
    }
    optimisticRollbacks.current.delete(rollbackId);

    setSummary((prev) => {
      const prevNutrition = prev.nutrition;
      if (!prevNutrition) return prev;
      const patched: DailySummary = {
        ...prev,
        nutrition: {
          ...prevNutrition,
          totalCalories: Math.max(0, (prevNutrition.totalCalories || 0) - delta.calories),
          totalProtein: Math.max(0, (prevNutrition.totalProtein || 0) - delta.protein),
          totalCarbs: Math.max(0, (prevNutrition.totalCarbs || 0) - delta.carbs),
          totalFat: Math.max(0, (prevNutrition.totalFat || 0) - delta.fat),
          totalFibre: Math.max(0, (prevNutrition.totalFibre || 0) - delta.fibre),
          totalHydration: Math.max(0, (prevNutrition.totalHydration || 0) - delta.hydrationMl),
        } as DailyNutrition,
      };
      globalCache = patched;
      saveTodaySnapshot(patched);
      return patched;
    });

    console.log(`[useDailySummary] Optimistic rollback (${rollbackId}):`, delta);
  }, []);

  return {
    ...summary,
    refreshDailySummary,
    invalidateCache,
    optimisticAddEntry,
    optimisticRollback,
  };
}
